import {ModelFilterTechnicalDataGroup} from '@volkswagen-onehub/car-technical-details-bff';
import {
  Accordion,
  styled,
  Text,
  TextTag,
  TokenTextAppearance,
} from '@volkswagen-onehub/components-core';
import * as React from 'react';
import {resolveDataGroupName} from '../utils/resolveDataGroupName';
import {useTrackingService} from '../utils/useTrackingService';

import {TechnicalDataSectionContent} from './section-content';
import {useTranslate} from '../utils/translate';
import {isI18nDebugEnabled} from '../utils/isI18nDebugEnabled';
import {ImageVisualisation} from './ImageVisualisation';
import {hideVisually} from './table';

// position: relative is needed as long as the table nav is broken
// the other styles are also just a short term solution for a particular styling choice
const StyledSection = styled.section`
  width: 100%;
  position: relative;

  & :where(th, td) {
    padding-inline: ${(props) => props.theme.size.static150};
  }

  & caption {
    ${hideVisually}
  }
`;

export function TechnicalDataSection(props: {
  group: ModelFilterTechnicalDataGroup;
  open: boolean;
  index: number;
  count: number;
}): JSX.Element | null {
  const {group, index, count} = props;
  const [isAccordionOpen, toggleAccordion] = React.useState(props.open);
  const trackingService = useTrackingService();
  const translate = useTranslate();
  const name = resolveDataGroupName(group, translate);

  const uniqueAriaId = name.replace(/ /g, '-');

  React.useEffect(() => {
    if (props.open) {
      trackingService.trackToggleAccordionClick({
        open: isAccordionOpen,
        name,
        position: index + 1,
        count,
        changedAutomatically: true,
      });
    }
  }, [count, index, isAccordionOpen, name, props.open, trackingService]);

  if (group.variations.length <= 0 || !name) {
    return null;
  }

  return (
    <StyledSection>
      <Accordion
        key={name}
        isOpen={isAccordionOpen}
        headline={
          <>
            <Text
              tag={TextTag.h3}
              appearance={TokenTextAppearance.headline200}
              bold
              wordBreak
            >
              {name}
            </Text>
            {isI18nDebugEnabled() && (
              <>
                <br></br>
                <Text
                  appearance={TokenTextAppearance.copy100}
                >{`groupKey=${group.key}`}</Text>
              </>
            )}
          </>
        }
        id={uniqueAriaId || ''}
        onClick={() => {
          toggleAccordion(!isAccordionOpen);
          trackingService.trackToggleAccordionClick({
            open: !isAccordionOpen,
            name,
            position: index + 1,
            count,
          });
        }}
      >
        {group.images ? <ImageVisualisation group={group} /> : null}
        <TechnicalDataSectionContent group={group} />
      </Accordion>
    </StyledSection>
  );
}
